import { z } from 'zod';
import { ALL_ROLES } from '../constants/roles.js';

export const ANNOUNCEMENT_TYPES = ['general', 'urgent', 'policy', 'event', 'holiday'] as const;
export const announcementTypeEnum = z.enum(ANNOUNCEMENT_TYPES);
export type AnnouncementType = z.infer<typeof announcementTypeEnum>;

/**
 * A company-wide (or role-targeted) broadcast, with per-user read tracking.
 * Ported from Office_project's `Announcement` model. `targetRoles: []` means
 * visible to every role — a non-empty list scopes visibility to just those
 * roles, enforced both on the list endpoint and (a gap closed while porting,
 * see announcement.service.ts's `requireVisible`) on direct-by-id fetches.
 */
export const createAnnouncementSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(200),
  content: z.string().trim().min(1, 'Content is required').max(5000),
  type: announcementTypeEnum.default('general'),
  targetRoles: z.array(z.enum(ALL_ROLES as [string, ...string[]])).default([]),
  isPinned: z.coerce.boolean().default(false),
  expiresAt: z.coerce.date().optional(),
});

export type CreateAnnouncementInput = z.infer<typeof createAnnouncementSchema>;

export const updateAnnouncementSchema = createAnnouncementSchema.partial();

export type UpdateAnnouncementInput = z.infer<typeof updateAnnouncementSchema>;

export const announcementListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  type: announcementTypeEnum.optional(),
  pinned: z.coerce.boolean().optional(),
});

export type AnnouncementListQuery = z.infer<typeof announcementListQuerySchema>;
